import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Divider, Space, Tooltip } from 'tdesign-react';
import { isVoid } from '@/utils/common';
import SvgIcon from '@/components/SvgIcon';

/**
 * @typedef {'blue' | 'yellow' | 'green'} themeEnum
 * @typedef {'topic' | 'subscription' | 'consumer' | 'producer' | 'other1' | 'other2' | 'other3' | 'other4','other5'} iconEnum
 */

/**
 *
 * @param {Object} props
 * @param {*} props.title
 * @param {*} props.info
 * @param {*} props.unit 单位
 * @param {Object[]} props.items
 * @param {string} props.items.label
 * @param {string} props.items.value
 * @param {string} props.items.tip
 * @param {themeEnum} props.theme
 * @param {iconEnum} props.icon
 * @returns
 */
export default function SummaryBlock(props) {
  const { title, info, unit, items = [], tip, theme, icon } = props;
  const [animation, setAnimation] = useState([
    { left: -125, top: 67 },
    { left: -70, top: 100 },
    { left: 76, top: -80, opacity: 0 },
    {
      left: 40,
      top: 18,
      opacity: 0,
    },
    'rgba(247, 248, 250, 0)',
    'rgba(247, 248, 250, 0)',
  ]);

  const iconImg = useMemo(() => {
    switch (icon) {
      case 'topic':
        return require('./images/img1.png');
      case 'subscription':
        return require('./images/img7.png');
      case 'consumer':
        return require('./images/img9.png');
      case 'producer':
        return require('./images/img6.png');
      case 'other1':
        return require('./images/img2.png');
      case 'other2':
        return require('./images/img3.png');
      case 'other3':
        return require('./images/img4.png');
      case 'other4':
        return require('./images/img5.png');
      case 'other5':
        return require('./images/img8.png');
      default:
        return null;
    }
  }, [icon]);

  const bgImage = useMemo(() => {
    if (theme === 'blue') {
      return [
        'linear-gradient(180deg, rgba(207, 237, 254, 0) 0%, #CFEDFE 100%)',
        'linear-gradient(180deg, rgba(207, 237, 254, 0) 0%, #CFEDFE 100%)',
        'linear-gradient(218deg, #CFEDFE 0%, rgba(207, 237, 254, 0) 100%)',
        'linear-gradient(131deg, rgba(176.37, 226.56, 255, 0) 0%, #CFEDFE 100%)',
      ];
    }

    if (theme === 'green') {
      return [
        'linear-gradient(180deg, rgba(207, 237, 254, 0) 0%, #CFEDFE 100%)',
        'linear-gradient(183deg, rgba(230.56, 255, 254.67, 0) 0%, rgba(36, 255, 252, 0.60) 100%)',
        'linear-gradient(218deg, rgba(36, 255, 252, 0.40) 0%, rgba(230.56, 255, 254.67, 0) 100%)',
        'linear-gradient(21deg, rgba(36, 255, 252, 0) 0%, #E1FFFF 100%)',
      ];
    }

    if (theme === 'yellow') {
      return [
        'linear-gradient(191deg, rgba(255, 154, 1, 0) 0%, rgba(255, 225.43, 180.63, 0.20) 100%)',
        'linear-gradient(191deg, rgba(255, 154, 1, 0) 0%, #FFE1B5 100%)',
        'linear-gradient(33deg, rgba(255, 154, 1, 0) 0%, rgba(255, 225.43, 180.63, 0.50) 100%)',
        'linear-gradient(136deg, rgba(255, 154, 1, 0) 0%, rgba(255, 225.43, 180.63, 0.80) 100%)',
      ];
    }

    return ['#F7F8FA', '#F7F8FA', '#F7F8FA', '#F7F8FA'];
  }, [theme]);

  useEffect(() => {
    setTimeout(() => {
      setAnimation([
        { left: -35, top: 47 },
        { left: -70, top: 36 },
        { left: 76, top: -58, opacity: 1 },
        {
          left: 40,
          top: 18,
          opacity: 1,
        },
        'rgba(247, 248, 250, 0.5)',
        'rgba(247, 248, 250, 1)',
      ]);
    }, 100);
  }, []);

  const background = useMemo(() => {
    let node = <Background style={{ backgroundColor: 'rgba(247, 248, 250, 1)' }} />;
    if (!isVoid(theme)) {
      node = (
        <Background style={{ backgroundColor: animation[4] }}>
          <BgBlock
            style={{
              ...animation[0],
              background: bgImage[0],
              transition: 'all 1.6s ease-in .1s',
            }}
          />
          <BgBlock
            style={{
              ...animation[1],
              background: bgImage[1],
              transition: 'all 1s ease-out',
            }}
          />
          <BgBlock
            style={{
              ...animation[2],
              transition: 'all 1.6s cubic-bezier(0.645, 0.045, 0.355, 1) .2s',
              background: bgImage[2],
            }}
          />
          <BgBlock
            style={{
              ...animation[3],
              background: bgImage[3],
              transition: 'all .6s ease-in',
            }}
          />
        </Background>
      );
    }

    if (!isVoid(icon)) {
      node = (
        <Background style={{ backgroundColor: animation[5], transition: 'all 2s ease-out' }}>
          <IconBackground className='animate__animated animate__fadeIn' />
          <IconBackground2
            src={require('./images/background.png')}
            className='animate__animated animate__fadeInRight'
          />
          <BgIcon src={iconImg} className='animate__animated animate__fadeInRight' />
        </Background>
      );
    }
    return node;
  }, [theme, icon, animation, bgImage, iconImg]);

  return (
    <Root>
      <Title>
        {title}&nbsp;
        {tip && (
          <Tooltip overlayInnerStyle={{ maxWidth: 600 }} content={<Tip>{tip}</Tip>} theme='light'>
            <span>
              <Icon size={14} type='exclamation-circle' />
            </span>
          </Tooltip>
        )}
      </Title>
      <Line style={{ justifyContent: isVoid(theme) ? 'flex-start' : 'space-between' }}>
        <CenterLine>
          <Info>{info}</Info>
          <Unit>{unit}</Unit>
        </CenterLine>
        <Space
          size={0}
          align='center'
          separator={<Divider style={{ margin: '0 6px' }} layout='vertical' />}
        >
          {items.map((item, index) => (
            <Item key={index}>
              {item.label}
              <B>{item.value}</B>
            </Item>
          ))}
        </Space>
      </Line>
      {background}
    </Root>
  );
}

const Root = styled.div`
  @keyframes shineSummaryBlock {
    100% {
      transform: translateX(400px) rotate(20deg);
    }
  }
  margin-top: 12px;
  border-radius: 6px;
  padding: 12px;
  box-sizing: border-box;
  position: relative;
  z-index: 1;
`;

const Background = styled.div`
  position: absolute;
  border-radius: 6px;
  overflow: hidden;
  transition: all 0.4s ease-out;
  left: 0;
  top: 0;
  bottom: 0;
  right: 0;
  z-index: -1;
  &:after {
    position: absolute;
    content: '';
    top: -5px;
    left: 0;
    height: 80px;
    width: 8px;
    background: rgba(255, 255, 255, 0.5);
    filter: blur(7px);
    transform: translateX(-15px) rotate(20deg);
    animation: shineSummaryBlock 1.2s linear 1.4s;
  }
`;

const IconBackground = styled.div`
  position: absolute;
  background: linear-gradient(270deg, #d1efff 0%, rgba(217, 217, 217, 0) 100%);
  left: 20%;
  top: 0;
  bottom: 0;
  right: 0;
  animation-duration: 3s;
  z-index: -2;
`;

const IconBackground2 = styled.img`
  position: absolute;
  width: 80%;
  height: 100%;
  top: 0;
  bottom: 0;
  right: 0;
  animation-duration: 2s;
  z-index: -1;
`;

const BgIcon = styled.img`
  width: 50px;
  height: auto;
  position: absolute;
  right: 20px;
  top: 0;
  bottom: 0;
  margin: auto;
  animation-duration: 0.8s;
  /* animation-delay: 0.5s; */
`;

const BgBlock = styled.div`
  border-radius: 100%;
  width: 100%;
  height: 150%;
  position: absolute;
`;

const Title = styled.div`
  font-size: 12px;
  line-height: 18px;
  color: #202b40;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
`;

const Icon = styled(SvgIcon)`
  color: var(--td-font-gray-2);
  cursor: pointer;
  margin-bottom: 2px;
`;

const Line = styled.div`
  display: flex;
  align-items: center;
`;

const CenterLine = styled.div`
  display: flex;
  align-items: flex-end;
  margin-right: 10px;
  min-width: 0;
`;

const Info = styled.div`
  font-size: 18px;
  line-height: 21px;
  color: #0d111a;
  margin-right: 4px;
  font-weight: bold;
  word-break: break-all;
  min-width: 0;
`;

const Unit = styled.div`
  font-size: 10px;
  line-height: 16px;
  color: #0d111a;
`;

const Item = styled.div`
  font-size: 12px;
  line-height: 17px;
  color: #8f959f;
`;

const B = styled.span`
  padding-left: 4px;
  color: var(--td-font-gray-1);
`;

const Tip = styled.div`
  padding: 6px;
`;
